import { createFileRoute, Link } from "@tanstack/react-router";
import { useI18n } from "@/lib/i18n";
import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  Copy,
  FileText,
  Film,
  FolderOpen,
  Images,
  Languages,
  Loader2,
  Mic,
  Palette,
  Play,
  Plus,
  ScrollText,
  Sparkles,
  Target,
  Trash2,
  Upload,
  Video,
  Wand2,
  CalendarPlus,
  Check,
  Download,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { TrialLimitBanner } from "@/components/TrialLimitBanner";
import { VideoLibrary } from "@/components/VideoLibrary";
import { AvatarCreatorCard } from "@/components/AvatarCreatorCard";
import { VoiceCloneCard } from "@/components/VoiceCloneCard";
import { usePlanAccess } from "@/hooks/usePlanAccess";
import { LockedFeatureCard } from "@/components/LockedFeatureCard";
import { BuyVideoCreditsDialog } from "@/components/BuyVideoCreditsDialog";
import { useVideoCredits } from "@/hooks/useVideoCredits";
import { AiImageAdCard } from "@/components/AiImageAdCard";
import { AiCarouselAdCard } from "@/components/AiCarouselAdCard";
import { AiCaptionWriterCard } from "@/components/AiCaptionWriterCard";
import { SpanishLipSyncCard } from "@/components/SpanishLipSyncCard";
import { LumaAnimateCard } from "@/components/LumaAnimateCard";
import { CinematicProCard } from "@/components/CinematicProCard";
import { AiVideoCreatorCard } from "@/components/AiVideoCreatorCard";
import { VideoLengthSelector, type VideoLength } from "@/components/VideoLengthSelector";
import { BrandLibraryButton, BrandLibraryPicker } from "@/components/BrandLibraryPicker";
import { AvatarVideoSelector, type AvatarConfig, type VoiceConfig } from "@/components/AvatarVideoSelector";
import { useBrandLibrary, BRAND_CATEGORIES, STORAGE_LIMIT_BYTES, formatBytes, type BrandCategory } from "@/hooks/useBrandLibrary";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/ai-studio")({
  component: AIStudioPage,
  head: () => ({
    meta: [
      { title: "AI Studio · AdKavi" },
      { name: "description", content: "Generate ad copy variations with AI." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type Variation = {
  angle: string;
  headline: string;
  primary_text: string;
  cta: string;
  hashtags?: string[];
};

const PLATFORMS = [
  { value: "meta", label: "Meta (Facebook / Instagram)" },
  { value: "tiktok", label: "TikTok" },
  { value: "google", label: "Google Search" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "youtube", label: "YouTube" },
];

const TONES = [
  { value: "confident", labelKey: "studio.tone.confident" },
  { value: "playful", labelKey: "studio.tone.playful" },
  { value: "luxury", labelKey: "studio.tone.luxury" },
  { value: "friendly", labelKey: "studio.tone.friendly" },
  { value: "urgent", labelKey: "studio.tone.urgent" },
  { value: "professional", labelKey: "studio.tone.professional" },
  { value: "bold", labelKey: "studio.tone.bold" },
];

const GOALS = [
  { value: "awareness", labelKey: "studio.goal.awareness" },
  { value: "traffic", labelKey: "studio.goal.traffic" },
  { value: "leads", labelKey: "studio.goal.leads" },
  { value: "sales", labelKey: "studio.goal.sales" },
  { value: "app_installs", labelKey: "studio.goal.appInstalls" },
];

type GuidedType = "campaign" | "post" | "video" | "spanish" | "tool";
type ActiveTab = "start" | "copy" | "images" | "videos" | "voice" | "library";

const GUIDED_OPTIONS: { id: GuidedType; emoji: string; titleKey: string; descKey: string }[] = [
  { id: "campaign", emoji: "🚀", titleKey: "studio.guided.campaign.title", descKey: "studio.guided.campaign.desc" },
  { id: "post",     emoji: "📱", titleKey: "studio.guided.post.title",     descKey: "studio.guided.post.desc" },
  { id: "video",    emoji: "🎬", titleKey: "studio.guided.video.title",    descKey: "studio.guided.video.desc" },
  { id: "spanish",  emoji: "🌎", titleKey: "studio.guided.spanish.title",  descKey: "studio.guided.spanish.desc" },
  { id: "tool",     emoji: "🛠️", titleKey: "studio.guided.tool.title",    descKey: "studio.guided.tool.desc" },
];

const TABS: { id: ActiveTab; labelKey: string }[] = [
  { id: "start",   labelKey: "studio.tab.start" },
  { id: "copy",    labelKey: "studio.tab.copy" },
  { id: "images",  labelKey: "studio.tab.images" },
  { id: "videos",  labelKey: "studio.tab.videos" },
  { id: "voice",   labelKey: "studio.tab.voice" },
  { id: "library", labelKey: "studio.tab.library" },
];

function AIStudioPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { hasFeature } = usePlanAccess();
  const canAvatar = hasFeature("ai_avatar");
  const canVoice = hasFeature("ai_voice");
  const canVideo = hasFeature("video_generation");
  const { credits, unlimited } = useVideoCredits();
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);
  const [product, setProduct] = useState("");
  const [audience, setAudience] = useState("");
  const [offer, setOffer] = useState("");
  const [tone, setTone] = useState("confident");
  const [platform, setPlatform] = useState("meta");
  const [goal, setGoal] = useState("sales");
  const [count, setCount] = useState("5");
  const [loading, setLoading] = useState(false);
  const [variations, setVariations] = useState<Variation[]>([]);

  const [activeTab, setActiveTab] = useState<ActiveTab>("start");
  const [guidedMode, setGuidedMode] = useState(false);
  const [guidedStep, setGuidedStep] = useState<1 | 2 | 3>(1);
  const [guidedType, setGuidedType] = useState<GuidedType | null>(null);

  const handleGenerate = async () => {
    if (!product.trim()) {
      toast.error(t("ai.toast.tellAdvertising"));
      return;
    }
    setLoading(true);
    setVariations([]);
    const { data, error } = await supabase.functions.invoke("generate-ad-copy", {
      body: {
        product: product.trim(),
        audience: audience.trim(),
        offer: offer.trim(),
        tone,
        platform,
        goal,
        count: Number(count) || 5,
      },
    });
    setLoading(false);

    if (error) {
      const status = (error as { context?: { status?: number } }).context?.status;
      if (status === 429) {
        toast.error(t("ai.toast.rateLimit"));
      } else if (status === 402) {
        toast.error(t("ai.toast.creditsExhausted"), {
          description: "Add credits in Settings → Workspace → Usage.",
        });
      } else {
        toast.error(t("ai.toast.generationFailed"), { description: error.message });
      }
      return;
    }
    if (data?.error) {
      toast.error(data.error);
      return;
    }
    const list: Variation[] = data?.variations ?? [];
    if (!list.length) {
      toast.error(t("ai.toast.noVariations"));
      return;
    }
    setVariations(list);
    toast.success(t("ai.toast.generated", { n: list.length }));
  };

  return (
    <section className="max-w-7xl mx-auto px-6 py-10">
      <TrialLimitBanner kind="ai_video" />
      <TrialLimitBanner kind="luma_video" />

      {/* Page header - always visible */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-orange-400" />
            <span className="bg-gradient-to-r from-orange-400 to-primary bg-clip-text text-transparent">AI Studio</span>
          </h1>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            {t("studio.subtitle")}
          </p>
        </div>
        <Link to="/creative" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors shrink-0">
          <Palette className="w-4 h-4" /> {t("studio.creativeBuilderLink")} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {/* Guided Campaign Flow */}
      {guidedMode ? (
        <GuidedPanel
          step={guidedStep}
          type={guidedType}
          onSelectType={setGuidedType}
          onNext={async () => {
            if (guidedStep === 1) {
              if (guidedType === "tool") { setGuidedMode(false); setGuidedStep(1); setGuidedType(null); setActiveTab("copy"); return; }
              if (guidedType) setGuidedStep(2);
            } else if (guidedStep === 2) {
              if (guidedType === "campaign" || guidedType === "post") {
                await handleGenerate();
                setGuidedStep(3);
              } else if (guidedType === "video") {
                setGuidedMode(false); setGuidedStep(1); setGuidedType(null); setActiveTab("videos");
              } else if (guidedType === "spanish") {
                setGuidedMode(false); setGuidedStep(1); setGuidedType(null); setActiveTab("voice");
              }
            } else {
              setGuidedMode(false); setGuidedStep(1); setGuidedType(null); setActiveTab("copy");
            }
          }}
          onBack={() => guidedStep > 1 ? setGuidedStep((s) => (s - 1) as 1 | 2 | 3) : (setGuidedMode(false), setGuidedStep(1), setGuidedType(null))}
          onExit={() => { setGuidedMode(false); setGuidedStep(1); setGuidedType(null); }}
          product={product} setProduct={setProduct}
          audience={audience} setAudience={setAudience}
          offer={offer} setOffer={setOffer}
          platform={platform} setPlatform={setPlatform}
          tone={tone} setTone={setTone}
          goal={goal} setGoal={setGoal}
          loading={loading}
          variations={variations}
          userId={user?.id}
          onGoToImages={() => { setGuidedMode(false); setGuidedStep(1); setGuidedType(null); setActiveTab("images"); }}
          onGoToVideos={() => { setGuidedMode(false); setGuidedStep(1); setGuidedType(null); setActiveTab("videos"); }}
        />
      ) : (
        <>
          {/* Tab navigation */}
          <div className="flex gap-0.5 overflow-x-auto pb-px mb-6 border-b border-border">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px rounded-t",
                  activeTab === tab.id
                    ? "border-orange-500 text-orange-400"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                {t(tab.labelKey)}
              </button>
            ))}
          </div>

          {/* Tab: Start Here */}
          {activeTab === "start" && (
            <StartHereTab
              onStartGuided={() => { setGuidedMode(true); setGuidedStep(1); setGuidedType(null); }}
              onBrowseTools={() => setActiveTab("copy")}
              onGoTo={setActiveTab}
            />
          )}

          {/* Tab: Ad Copy */}
          {activeTab === "copy" && (
            <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
              {/* Sticky form panel */}
              <div className="rounded-2xl border border-border bg-card p-6 space-y-5 h-fit lg:sticky lg:top-6">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <FileText className="w-4 h-4 text-primary" /> {t("studio.adWriter.title")}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ai-product">{t("studio.adWriter.productLabel")}</Label>
                  <Textarea id="ai-product" value={product} onChange={(e) => setProduct(e.target.value)}
                    placeholder="e.g. A 12-week online course that teaches beginners to code in Python." rows={3} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ai-audience">{t("studio.adWriter.audienceLabel")}</Label>
                  <Input id="ai-audience" value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Career changers, 25–40, in the US" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ai-offer">{t("studio.adWriter.offerLabel")}</Label>
                  <Input id="ai-offer" value={offer} onChange={(e) => setOffer(e.target.value)} placeholder="Free 7-day trial, 30% off this week" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>{t("studio.adWriter.platformLabel")}</Label>
                    <Select value={platform} onValueChange={setPlatform}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("studio.adWriter.goalLabel")}</Label>
                    <Select value={goal} onValueChange={setGoal}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{GOALS.map((g) => <SelectItem key={g.value} value={g.value}>{t(g.labelKey)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>{t("studio.adWriter.toneLabel")}</Label>
                    <Select value={tone} onValueChange={setTone}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{TONES.map((tn) => <SelectItem key={tn.value} value={tn.value}>{t(tn.labelKey)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("studio.adWriter.variationsLabel")}</Label>
                    <Select value={count} onValueChange={setCount}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{[3, 5, 8].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={handleGenerate} disabled={loading} className="w-full rounded-full bg-orange-500 hover:bg-orange-600 text-white">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("studio.adWriter.generating")}</> : <><Wand2 className="w-4 h-4 mr-2" /> {t("studio.adWriter.generateBtn")}</>}
                </Button>
                <p className="text-[11px] text-muted-foreground">{t("studio.adWriter.poweredBy")}</p>
              </div>

              {/* Results */}
              <div>
                {loading && variations.length === 0 && <SkeletonGrid />}
                {!loading && variations.length === 0 && <EmptyState />}
                {variations.length > 0 && (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm text-muted-foreground">{t("studio.adWriter.variationsCount").replace("{n}", String(variations.length))}</p>
                      <Link to="/creative" className="inline-flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 text-sm font-semibold transition-colors">
                        <Palette className="w-4 h-4" /> {t("studio.adWriter.openInCreative")} <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {variations.map((v, i) => <VariationCard key={i} v={v} platform={platform} userId={user?.id} />)}
                    </div>
                  </>
                )}
                {/* Caption Writer + Script Writer below results */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  <AiCaptionWriterCard />
                  <ScriptWriterCard />
                </div>
              </div>
            </div>
          )}

          {/* Tab: Images */}
          {activeTab === "images" && (
            <div>
              <p className="text-sm text-muted-foreground mb-6">{t("studio.images.subtitle")}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <AiImageAdCard />
                <AiCarouselAdCard />
                <LumaAnimateCard />
              </div>
            </div>
          )}

          {/* Tab: Videos */}
          {activeTab === "videos" && (
            <div>
              <div className="mb-4 rounded-xl border border-border bg-muted/40 p-4 text-xs text-muted-foreground leading-relaxed">
                <strong className="text-foreground">AI video credits policy:</strong> Credits are non-refundable once used. A credit is consumed the moment a video begins generating. Each generation includes <strong className="text-foreground">one free regeneration</strong>. See <a href="/terms" className="text-primary hover:underline">Terms</a> for details.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {canVideo ? (
                  <>
                    <AiVideoCreatorCard credits={credits} unlimited={unlimited} onNoCredits={() => setBuyCreditsOpen(true)} />
                    <AvatarVideoCard userId={user?.id} credits={credits} unlimited={unlimited} onNoCredits={() => setBuyCreditsOpen(true)} onGoToVoice={() => setActiveTab("voice")} />
                    <LumaVideoCard userId={user?.id} credits={credits} unlimited={unlimited} onNoCredits={() => setBuyCreditsOpen(true)} />
                    <CinematicProCard />
                  </>
                ) : (
                  <LockedFeatureCard title={t("studio.videos.lockedTitle")} requiredPlan="Dominate" description={t("studio.videos.lockedDesc")} />
                )}
              </div>
            </div>
          )}

          {/* Tab: Voice + Spanish */}
          {activeTab === "voice" && (
            <div>
              <p className="text-sm text-muted-foreground mb-6">{t("studio.voice.subtitle")}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {canVoice ? <VoiceCloneCard /> : <LockedFeatureCard title={t("studio.voice.voiceCloneTitle")} requiredPlan="Dominate" />}
                {canVideo ? <VoiceoverCard userId={user?.id} /> : <LockedFeatureCard title={t("studio.voice.voGeneratorTitle")} requiredPlan="Dominate" />}
                {canVideo ? <SpanishDubCard /> : <LockedFeatureCard title={t("studio.voice.dubTitle")} requiredPlan="Dominate" />}
                {canVideo ? <SpanishLipSyncCard /> : null}
              </div>
            </div>
          )}

          {/* Tab: My Library */}
          {activeTab === "library" && (
            <div className="space-y-6">
              {/* My Videos */}
              <div>
                {canVideo ? (
                  <VideoLibrary />
                ) : (
                  <div className="rounded-2xl border border-dashed border-border/60 bg-card/30 p-16 text-center">
                    <FolderOpen className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">{t("studio.library.emptyTitle")}</h3>
                    <p className="text-sm text-muted-foreground">{t("studio.library.emptyDesc")}</p>
                  </div>
                )}
              </div>

              {/* Voice Clone */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {canVoice ? <VoiceCloneCard /> : <LockedFeatureCard title={t("studio.voice.voiceCloneTitle")} requiredPlan="Dominate" />}
                {canAvatar ? <AvatarCreatorCard /> : <LockedFeatureCard title={t("studio.voice.avatarSetupTitle")} requiredPlan="Dominate" />}
              </div>

              {/* Video Credits */}
              {!unlimited && (
                <div className="rounded-xl border border-orange-500/20 bg-orange-500/5 p-4 flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold">{t("studio.library.creditsTitle")}</div>
                    <div className="text-2xl font-bold text-orange-400 mt-0.5">{credits ?? 0}</div>
                  </div>
                  <Button onClick={() => setBuyCreditsOpen(true)} className="rounded-full bg-orange-500 hover:bg-orange-600 text-white">
                    {t("studio.library.buyCredits")}
                  </Button>
                </div>
              )}

              {/* Brand Library */}
              <BrandLibraryManager />
            </div>
          )}
        </>
      )}

      <BuyVideoCreditsDialog open={buyCreditsOpen} onOpenChange={setBuyCreditsOpen} />
    </section>
  );
}

function StartHereTab({
  onStartGuided,
  onBrowseTools,
  onGoTo,
}: {
  onStartGuided: () => void;
  onBrowseTools: () => void;
  onGoTo: (tab: ActiveTab) => void;
}) {
  const { t } = useI18n();
  const TOOL_CARDS: { tab: ActiveTab; emoji: string; nameKey: string; descKey: string }[] = [
    { tab: "copy",    emoji: "✍️", nameKey: "studio.tool.adWriter.name",    descKey: "studio.tool.adWriter.desc" },
    { tab: "copy",    emoji: "📝", nameKey: "studio.tool.caption.name",     descKey: "studio.tool.caption.desc" },
    { tab: "copy",    emoji: "🎬", nameKey: "studio.tool.scriptWriter.name", descKey: "studio.tool.scriptWriter.desc" },
    { tab: "images",  emoji: "🖼️", nameKey: "studio.tool.imageAds.name",    descKey: "studio.tool.imageAds.desc" },
    { tab: "images",  emoji: "🗂️", nameKey: "studio.tool.carousel.name",    descKey: "studio.tool.carousel.desc" },
    { tab: "videos",  emoji: "🎞️", nameKey: "studio.tool.aiVideoCreator.name", descKey: "studio.tool.aiVideoCreator.desc" },
    { tab: "videos",  emoji: "🤖", nameKey: "studio.tool.talkingVideo.name",   descKey: "studio.tool.talkingVideo.desc" },
    { tab: "videos",  emoji: "🎬", nameKey: "studio.tool.cinematicPro.name",  descKey: "studio.tool.cinematicPro.desc" },
    { tab: "videos",  emoji: "🌅", nameKey: "studio.tool.lifestyleVideo.name", descKey: "studio.tool.lifestyleVideo.desc" },
    { tab: "voice",   emoji: "🎙️", nameKey: "studio.tool.voiceover.name",   descKey: "studio.tool.voiceover.desc" },
    { tab: "voice",   emoji: "🌎", nameKey: "studio.tool.dub.name",         descKey: "studio.tool.dub.desc" },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
      <div>
        {/* Hero */}
        <div className="rounded-2xl border border-orange-500/20 bg-gradient-to-br from-orange-500/8 to-primary/5 p-8 text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-bold mb-2">{t("studio.start.heroTitle")}</h2>
          <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
            {t("studio.start.heroSubtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={onStartGuided}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white px-7 py-3 text-sm font-semibold transition-colors"
            >
              <Sparkles className="w-4 h-4" /> {t("studio.start.startGuided")}
            </button>
            <button
              onClick={onBrowseTools}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-card hover:border-primary/40 hover:text-foreground px-7 py-3 text-sm font-semibold transition-colors text-muted-foreground"
            >
              {t("studio.start.browseTools")}
            </button>
          </div>
        </div>

        {/* Tool quick-access grid */}
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{t("studio.start.quickAccess")}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {TOOL_CARDS.map((tool) => (
            <button
              key={tool.nameKey}
              onClick={() => onGoTo(tool.tab)}
              className="text-left rounded-xl border border-border bg-card p-4 hover:border-primary/40 hover:bg-card/80 transition-colors group"
            >
              <div className="text-2xl mb-2">{tool.emoji}</div>
              <div className="font-semibold text-sm group-hover:text-primary transition-colors">{t(tool.nameKey)}</div>
              <div className="text-xs text-muted-foreground mt-1">{t(tool.descKey)}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Not sure where to start? guide box */}
      <div className="rounded-2xl border border-border bg-card p-6 h-fit">
        <div className="flex items-center gap-2 mb-4">
          <Target className="w-5 h-5 text-orange-400" />
          <h3 className="font-semibold text-base">{t("studio.start.guideTitle")}</h3>
        </div>
        <ul className="space-y-3 text-sm text-muted-foreground mb-6">
          <li><strong className="text-foreground">{t("studio.start.guide.fullCampaign.label")}</strong> — {t("studio.start.guide.fullCampaign.desc")}</li>
          <li><strong className="text-foreground">{t("studio.start.guide.adCopy.label")}</strong> — {t("studio.start.guide.adCopy.desc")}</li>
          <li><strong className="text-foreground">{t("studio.start.guide.images.label")}</strong> — {t("studio.start.guide.images.desc")}</li>
          <li><strong className="text-foreground">{t("studio.start.guide.videos.label")}</strong> — {t("studio.start.guide.videos.desc")}</li>
          <li><strong className="text-foreground">{t("studio.start.guide.voice.label")}</strong> — {t("studio.start.guide.voice.desc")}</li>
        </ul>
        <button
          onClick={onStartGuided}
          className="w-full rounded-full bg-orange-500 hover:bg-orange-600 text-white py-2.5 text-sm font-semibold transition-colors"
        >
          {t("studio.start.startSetup")}
        </button>
      </div>
    </div>
  );
}

function GuidedPanel({
  step, type, onSelectType, onNext, onBack, onExit,
  product, setProduct, audience, setAudience, offer, setOffer,
  platform, setPlatform, tone, setTone, goal, setGoal,
  loading, variations, userId, onGoToImages, onGoToVideos,
}: {
  step: 1 | 2 | 3;
  type: GuidedType | null;
  onSelectType: (t: GuidedType) => void;
  onNext: () => Promise<void>;
  onBack: () => void;
  onExit: () => void;
  product: string; setProduct: (v: string) => void;
  audience: string; setAudience: (v: string) => void;
  offer: string; setOffer: (v: string) => void;
  platform: string; setPlatform: (v: string) => void;
  tone: string; setTone: (v: string) => void;
  goal: string; setGoal: (v: string) => void;
  loading: boolean;
  variations: Variation[];
  userId?: string;
  onGoToImages: () => void;
  onGoToVideos: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {step > 1 && (
            <button onClick={onBack} className="rounded-full border border-border p-1.5 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-orange-400 mb-0.5">{t("studio.guided.header")}</div>
            <div className="font-semibold text-lg">
              {step === 1 && t("studio.guided.step1Title")}
              {step === 2 && t("studio.guided.step2Title")}
              {step === 3 && t("studio.guided.step3Title")}
            </div>
          </div>
        </div>
        <button onClick={onExit} className="text-muted-foreground hover:text-foreground transition-colors p-1">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Step 1: Goal selection */}
      {step === 1 && (
        <div>
          <p className="text-sm text-muted-foreground mb-5">{t("studio.guided.step1Desc")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            {GUIDED_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                onClick={() => onSelectType(opt.id)}
                className={cn(
                  "text-left rounded-xl border p-5 transition-colors",
                  type === opt.id
                    ? "border-orange-500 bg-orange-500/8"
                    : "border-border bg-background hover:border-primary/40"
                )}
              >
                <div className="text-2xl mb-2">{opt.emoji}</div>
                <div className="font-semibold text-sm mb-1">{t(opt.titleKey)}</div>
                <div className="text-xs text-muted-foreground">{t(opt.descKey)}</div>
              </button>
            ))}
          </div>
          <Button
            onClick={onNext}
            disabled={!type}
            className="rounded-full bg-orange-500 hover:bg-orange-600 text-white px-8"
          >
            {t("studio.guided.continue")} <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Step 2: Details form */}
      {step === 2 && (
        <div className="max-w-2xl">
          <p className="text-sm text-muted-foreground mb-5">
            {type === "campaign" && t("studio.guided.step2.campaign")}
            {type === "post" && t("studio.guided.step2.post")}
            {type === "video" && t("studio.guided.step2.video")}
            {type === "spanish" && t("studio.guided.step2.spanish")}
          </p>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("studio.adWriter.productLabel")}</Label>
              <Textarea value={product} onChange={(e) => setProduct(e.target.value)}
                placeholder="e.g. A skincare line for sensitive skin, 30% off this week." rows={3} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("studio.guided.audienceLabel")}</Label>
              <Input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Women 25–45 in the US" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("studio.guided.offerLabel")}</Label>
              <Input value={offer} onChange={(e) => setOffer(e.target.value)} placeholder="Free 7-day trial, limited time offer" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("studio.adWriter.platformLabel")}</Label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("studio.adWriter.toneLabel")}</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TONES.map((tn) => <SelectItem key={tn.value} value={tn.value}>{t(tn.labelKey)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <Button
              onClick={onNext}
              disabled={loading || !product.trim()}
              className="rounded-full bg-orange-500 hover:bg-orange-600 text-white px-8"
            >
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("studio.adWriter.generating")}</> : <><Wand2 className="w-4 h-4 mr-2" /> {t("studio.guided.continue")}</>}
            </Button>
            <Button variant="outline" onClick={onBack} className="rounded-full">{t("studio.guided.back")}</Button>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && (
        <div>
          {loading && <SkeletonGrid />}
          {!loading && variations.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {variations.slice(0, 4).map((v, i) => <VariationCard key={i} v={v} platform={platform} userId={userId} />)}
              </div>
              <div className="rounded-xl border border-border bg-muted/30 p-5">
                <div className="font-semibold text-sm mb-3">{t("studio.guided.step3NextTitle")}</div>
                <div className="flex flex-wrap gap-3">
                  <button onClick={onGoToImages} className="inline-flex items-center gap-2 rounded-full border border-border bg-card hover:border-primary/40 px-4 py-2 text-sm font-medium transition-colors">
                    {t("studio.guided.step3GenVisuals")}
                  </button>
                  <button onClick={onGoToVideos} className="inline-flex items-center gap-2 rounded-full border border-border bg-card hover:border-primary/40 px-4 py-2 text-sm font-medium transition-colors">
                    {t("studio.guided.step3CreateVideo")}
                  </button>
                  <Link to="/creative" className="inline-flex items-center gap-2 rounded-full border border-border bg-card hover:border-primary/40 px-4 py-2 text-sm font-medium transition-colors">
                    <Palette className="w-4 h-4" /> {t("studio.guided.step3Design")}
                  </Link>
                </div>
              </div>
              <div className="mt-4 flex gap-3">
                <Button onClick={onExit} className="rounded-full bg-orange-500 hover:bg-orange-600 text-white">
                  {t("studio.guided.step3ContinueBtn")}
                </Button>
                <Button variant="outline" onClick={onExit} className="rounded-full">{t("studio.guided.step3ExitBtn")}</Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function VariationCard({
  v,
  platform,
  userId,
}: {
  v: Variation;
  platform: string;
  userId?: string;
}) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  const hashtagLine = v.hashtags?.length ? "\n\n" + v.hashtags.map((h) => `#${h}`).join(" ") : "";
  const fullText = `${v.headline}\n\n${v.primary_text}${hashtagLine}\n\nCTA: ${v.cta}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success(t("common.copied"));
    } catch {
      toast.error(t("common.copyFailed"));
    }
  };

  const handleSchedule = async () => {
    if (!userId) {
      toast.error(t("common.signInToSchedule"));
      return;
    }
    const platformMap: Record<string, string> = {
      meta: "instagram",
      tiktok: "tiktok",
      google: "facebook",
      linkedin: "linkedin",
      youtube: "youtube",
    };
    setSaving(true);
    const when = new Date();
    when.setDate(when.getDate() + 1);
    when.setHours(9, 0, 0, 0);
    const { error } = await supabase.from("scheduled_posts").insert({
      user_id: userId,
      title: v.headline.slice(0, 80),
      caption: `${v.primary_text}\n\nCTA: ${v.cta}`,
      platform: (platformMap[platform] ?? "instagram") as
        | "instagram"
        | "facebook"
        | "tiktok"
        | "linkedin"
        | "twitter"
        | "youtube",
      kind: "post",
      status: "draft",
      scheduled_at: when.toISOString(),
      client_id: (typeof window !== "undefined" ? window.localStorage.getItem("adkavi.activeClientId") : null) ?? null,
    });
    setSaving(false);
    if (error) {
      toast.error(t("common.saveFailed"), { description: error.message });
      return;
    }
    toast.success(t("ai.toast.savedDraft"));
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 flex flex-col">
      <Badge variant="outline" className="self-start text-[10px] uppercase tracking-wider mb-3">
        {v.angle}
      </Badge>
      <h4 className="font-semibold text-base leading-snug">{v.headline}</h4>
      <p className="mt-3 text-sm text-muted-foreground leading-relaxed whitespace-pre-line flex-1">
        {v.primary_text}
      </p>
      <div className="mt-4 inline-flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">CTA:</span>
        <span className="px-2.5 py-1 rounded-full bg-primary/15 text-primary font-medium">{v.cta}</span>
      </div>
      {v.hashtags?.length ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {v.hashtags.map((h) => (
            <span key={h} className="text-[11px] text-primary/80 bg-primary/5 px-2 py-0.5 rounded-full">
              #{h}
            </span>
          ))}
        </div>
      ) : null}
      <div className="mt-5 flex gap-2">
        <Button variant="outline" size="sm" className="flex-1" onClick={handleCopy}>
          {copied ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
          {copied ? t("studio.var.copied") : t("studio.var.copy")}
        </Button>
        <Button variant="outline" size="sm" className="flex-1" onClick={handleSchedule} disabled={saving}>
          {saving ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <CalendarPlus className="w-3.5 h-3.5 mr-1.5" />
          )}
          {t("studio.var.saveToCalendar")}
        </Button>
      </div>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="rounded-2xl border border-border bg-card p-5 animate-pulse">
          <div className="h-4 w-20 bg-muted rounded mb-4" />
          <div className="h-5 w-3/4 bg-muted rounded mb-3" />
          <div className="space-y-2">
            <div className="h-3 w-full bg-muted rounded" />
            <div className="h-3 w-full bg-muted rounded" />
            <div className="h-3 w-2/3 bg-muted rounded" />
          </div>
          <div className="h-7 w-24 bg-muted rounded-full mt-5" />
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  const { t } = useI18n();
  return (
    <div className="rounded-2xl border border-dashed border-border/60 bg-card/30 p-12 text-center">
      <Sparkles className="w-12 h-12 text-primary/60 mx-auto mb-4" />
      <h3 className="text-xl font-semibold">{t("studio.empty.title")}</h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
        {t("studio.empty.desc")}
      </p>
      <div className="mt-6 flex items-center justify-center gap-3 text-xs text-muted-foreground">
        <span>{t("studio.empty.alreadyHave")}</span>
        <Link
          to="/creative"
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium hover:border-primary/40 hover:text-foreground transition-colors"
        >
          <Palette className="w-3.5 h-3.5" /> {t("studio.empty.creativeBuilderLink")}
        </Link>
      </div>
    </div>
  );
}

async function logGeneration(
  userId: string | undefined,
  kind: "video" | "voice" | "luma_video",
  outputUrl: string | null,
  prompt: string,
  parentId: string | null = null,
  meta: { duration_seconds?: number | null; provider?: string | null } = {},
): Promise<string | null> {
  if (!userId) return null;
  const clientId = typeof window !== "undefined" ? window.localStorage.getItem("adkavi.activeClientId") : null;

  const { data } = await supabase
    .from("ai_generations")
    .insert({
      user_id: userId,
      client_id: clientId,
      kind,
      output_url: outputUrl,
      prompt: prompt.slice(0, 500),
      parent_id: parentId,
      status: "ready",
      provider: meta.provider ?? (kind === "luma_video" ? "luma" : kind === "video" ? "heygen" : null),
      duration_seconds: meta.duration_seconds ?? null,
    })
    .select("id")
    .single();

  if (parentId) {
    const { data: parent } = await supabase
      .from("ai_generations")
      .select("regen_count")
      .eq("id", parentId)
      .single();
    const nextCount = (parent?.regen_count ?? 0) + 1;
    await supabase
      .from("ai_generations")
      .update({
        regen_count: nextCount,
        // Free regen (first one) soft-replaces the original — original is hidden from the library
        ...(nextCount === 1 && data?.id ? { replaced_by_id: data.id } : {}),
      })
      .eq("id", parentId);
  }
  return data?.id ?? null;
}

function AvatarVideoCard({
  userId,
  credits,
  unlimited,
  onNoCredits,
  onGoToVoice,
}: {
  userId?: string;
  credits?: number;
  unlimited?: boolean;
  onNoCredits?: () => void;
  onGoToVoice?: () => void;
}) {
  const { t } = useI18n();
  const [script, setScript] = useState("");
  const [videoLength, setVideoLength] = useState<VideoLength>("auto");
  const [avatar, setAvatar] = useState<AvatarConfig | null>(null);
  const [voice, setVoice] = useState<VoiceConfig | null>(null);
  const [savedAvatar, setSavedAvatar] = useState<{
    id: string; type: string; gender: string; label: string; thumbnail?: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [parentId, setParentId] = useState<string | null>(null);
  const [regenUsed, setRegenUsed] = useState(false);
  const pollRef = useRef<number | null>(null);

  // Load saved avatar from profile on mount
  useEffect(() => {
    if (!userId) return;
    supabase
      .from("profiles")
      .select("my_avatar_id, my_avatar_type, my_avatar_gender, my_avatar_label, my_avatar_thumbnail")
      .eq("user_id", userId)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.my_avatar_id) {
          setSavedAvatar({
            id: data.my_avatar_id,
            type: data.my_avatar_type ?? "talking_photo",
            gender: data.my_avatar_gender ?? "female",
            label: data.my_avatar_label ?? "My Avatar",
            thumbnail: data.my_avatar_thumbnail ?? undefined,
          });
        }
      });
  }, [userId]);

  useEffect(() => () => {
    if (pollRef.current) window.clearInterval(pollRef.current);
  }, []);

  const startGeneration = async (isRegen: boolean) => {
    if (!script.trim()) {
      toast.error(t("ai.toast.addAvatarScript"));
      return;
    }
    if (!voice) {
      toast.error("Please choose a voice for this video");
      return;
    }
    if (!unlimited && (credits ?? 0) === 0) {
      onNoCredits?.();
      return;
    }
    if (isRegen && regenUsed) {
      const ok = window.confirm(t("studio.avatar.regenConfirm"));
      if (!ok) return;
    }
    setVideoUrl(null);
    setLoading(true);

    const body: Record<string, unknown> = {
      action: "create",
      script: script.trim(),
      elevenLabsVoiceId: voice.elevenLabsVoiceId,
    };
    if (avatar) {
      body.avatarType = avatar.type;
      body.avatarId = avatar.id;
      // PATH 2 — photo lipsync via fal.ai sadtalker
      if (avatar.type === "photo") {
        body.avatarImageUrl = avatar.photoUrl;
        body.avatarImageBase64 = avatar.photoBase64;
        body.avatarImageContentType = avatar.photoContentType;
      }
    }

    const { data, error } = await supabase.functions.invoke("generate-avatar-video", { body });
    setLoading(false);
    if (error || data?.error) {
      toast.error(t("ai.toast.startVideoFailed"), { description: data?.error ?? error?.message });
      return;
    }
    const id = data?.video_id as string | undefined;
    if (!id) {
      toast.error(t("ai.toast.heygenNoId"));
      return;
    }
    const provider = (data?.provider as string) ?? "heygen";
    toast.success(
      isRegen
        ? regenUsed
          ? t("studio.avatar.regenCreditUsed")
          : t("studio.avatar.regenFree")
        : t("studio.avatar.rendering"),
    );
    setPolling(true);
    pollRef.current = window.setInterval(async () => {
      const { data: s } = await supabase.functions.invoke("generate-avatar-video", {
        body: { action: "status", videoId: id },
      });
      if (s?.status === "completed" && s?.video_url) {
        if (pollRef.current) window.clearInterval(pollRef.current);
        setPolling(false);
        setVideoUrl(s.video_url);
        const newId = await logGeneration(userId, "video", s.video_url, script, isRegen ? parentId : null, { provider });
        if (isRegen) {
          setRegenUsed(true);
        } else {
          setParentId(newId);
          setRegenUsed(false);
        }
        toast.success(t("ai.toast.videoReady"));
      } else if (s?.status === "failed") {
        if (pollRef.current) window.clearInterval(pollRef.current);
        setPolling(false);
        toast.error(t("ai.toast.heygenFailed"), { description: s?.error ?? t("ai.toast.heygenShorter") });
      }
    }, 6000);
  };

  const busy = loading || polling;

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="mb-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Video className="w-5 h-5 text-primary" />
        </div>
      </div>
      <h3 className="font-semibold">{t("studio.avatar.title")}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{t("studio.avatar.desc")}</p>

      <Textarea
        className="mt-4"
        rows={3}
        value={script}
        onChange={(e) => setScript(e.target.value)}
        placeholder="Hi, I'm your AI marketing assistant from AdKavi…"
        disabled={busy}
      />
      {videoLength !== "auto" && (
        <p className="mt-2 text-[11px] text-orange-400/80">{t(`studio.avatar.hint${videoLength}`)}</p>
      )}

      <div className="mt-3">
        <VideoLengthSelector value={videoLength} onChange={setVideoLength} disabled={busy} />
      </div>

      {/* Avatar + Voice selector */}
      <AvatarVideoSelector
        avatar={avatar}
        voice={voice}
        onAvatarChange={setAvatar}
        onVoiceChange={setVoice}
        disabled={busy}
        savedAvatar={savedAvatar}
        onTabSwitch={onGoToVoice ? () => onGoToVoice() : undefined}
      />

      <Button
        onClick={() => startGeneration(false)}
        disabled={busy || !voice}
        className="w-full mt-4 rounded-full bg-orange-500 hover:bg-orange-600 text-white"
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("studio.avatar.submitting")}</>
        ) : polling ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("studio.avatar.renderingBtn")}</>
        ) : (
          <><Wand2 className="w-4 h-4 mr-2" /> {t("studio.avatar.generateBtn")}</>
        )}
      </Button>

      {videoUrl && (
        <div className="mt-4">
          <video src={videoUrl} controls className="w-full rounded-xl border border-border" />
          <div className="mt-2 flex items-center justify-between gap-2">
            <a
              href={videoUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center text-xs text-primary hover:underline"
            >
              <Download className="w-3.5 h-3.5 mr-1" /> {t("studio.common.download")}
            </a>
            <Button
              variant="outline"
              size="sm"
              onClick={() => startGeneration(true)}
              disabled={busy}
              className="text-xs"
            >
              <Wand2 className="w-3.5 h-3.5 mr-1.5" />
              {regenUsed ? t("studio.regen.credit") : t("studio.regen.free")}
            </Button>
          </div>
        </div>
      )}
      <p className="mt-3 text-[10px] text-muted-foreground/60 uppercase tracking-wider">{t("studio.avatar.poweredBy")}</p>
    </div>
  );
}

function VoiceoverCard({ userId }: { userId?: string }) {
  const { t } = useI18n();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!text.trim()) {
      toast.error(t("ai.toast.addNarrationText"));
      return;
    }
    setLoading(true);
    setAudioUrl(null);
    const { data, error } = await supabase.functions.invoke("generate-voiceover", {
      body: { text: text.trim() },
    });
    setLoading(false);
    if (error || data?.error) {
      toast.error(t("ai.toast.voFailed"), { description: data?.error ?? error?.message });
      return;
    }
    if (!data?.audio) {
      toast.error(t("ai.toast.noAudio"));
      return;
    }
    const url = `data:${data.mime ?? "audio/mpeg"};base64,${data.audio}`;
    setAudioUrl(url);
    logGeneration(userId, "voice", null, text);
    toast.success(t("ai.toast.voReady"));
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="mb-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Mic className="w-5 h-5 text-primary" />
        </div>
      </div>
      <h3 className="font-semibold">{t("studio.vo.title")}</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {t("studio.vo.desc")}
      </p>
      <Textarea
        className="mt-4"
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Spring is here — and so is our biggest sale of the year."
      />
      <Button
        onClick={handleGenerate}
        disabled={loading}
        className="w-full mt-3 rounded-full bg-orange-500 hover:bg-orange-600 text-white"
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("studio.adWriter.generating")}</>
        ) : (
          <><Play className="w-4 h-4 mr-2" /> {t("studio.vo.generateBtn")}</>
        )}
      </Button>
      {audioUrl && (
        <div className="mt-4">
          <audio src={audioUrl} controls className="w-full" />
          <a
            href={audioUrl}
            download="adkavi-voiceover.mp3"
            className="mt-2 inline-flex items-center text-xs text-primary hover:underline"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> {t("studio.vo.downloadMp3")}
          </a>
        </div>
      )}
      <p className="mt-3 text-[10px] text-muted-foreground/60 uppercase tracking-wider">{t("studio.vo.poweredBy")}</p>
    </div>
  );
}

const LUMA_DURATION: Record<VideoLength, number> = {
  auto: 5,
  "15": 10,
  "30": 10,
  "60": 10,
  "90": 10,
};

function LumaVideoCard({ userId, credits, unlimited, onNoCredits }: { userId?: string; credits?: number; unlimited?: boolean; onNoCredits?: () => void }) {
  const { t } = useI18n();
  const [prompt, setPrompt] = useState("");
  const [aspect, setAspect] = useState("16:9");
  const [videoLength, setVideoLength] = useState<VideoLength>("auto");
  const [startingImageUrl, setStartingImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [mocked, setMocked] = useState(false);
  const [parentId, setParentId] = useState<string | null>(null);
  const [regenUsed, setRegenUsed] = useState(false);

  const startGeneration = async (isRegen: boolean) => {
    if (!prompt.trim()) {
      toast.error(t("ai.toast.describeClip"));
      return;
    }
    if (!unlimited && (credits ?? 0) === 0) {
      onNoCredits?.();
      return;
    }
    if (isRegen && regenUsed) {
      const ok = window.confirm(t("studio.luma.regenConfirm"));
      if (!ok) return;
    }
    const duration = LUMA_DURATION[videoLength];
    setLoading(true);
    setVideoUrl(null);
    const { data, error } = await supabase.functions.invoke("generate-luma-video", {
      body: {
        prompt: prompt.trim(),
        aspect_ratio: aspect,
        duration,
        ...(startingImageUrl ? { image_url: startingImageUrl } : {}),
      },
    });
    setLoading(false);
    if (error || data?.error) {
      toast.error(t("ai.toast.clipFailed"), { description: data?.error ?? error?.message });
      return;
    }
    if (!data?.video_url) {
      toast.error(t("ai.toast.noVideo"));
      return;
    }
    setVideoUrl(data.video_url);
    setMocked(Boolean(data.mocked));
    const newId = await logGeneration(
      userId,
      "luma_video",
      data.video_url,
      prompt,
      isRegen ? parentId : null,
      { duration_seconds: duration, provider: "luma" },
    );
    if (isRegen) {
      setRegenUsed(true);
    } else {
      setParentId(newId);
      setRegenUsed(false);
    }
    toast.success(
      data.mocked
        ? t("studio.luma.toastMock")
        : isRegen
          ? regenUsed
            ? t("studio.luma.toastRegenCredit")
            : t("studio.luma.toastRegenFree")
          : t("studio.luma.toastReady"),
    );
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="mb-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Film className="w-5 h-5 text-primary" />
        </div>
      </div>
      <h3 className="font-semibold">{t("studio.luma.title")}</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {t("studio.luma.desc")}
      </p>
      <Textarea
        className="mt-4"
        rows={3}
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="Slow dolly shot through a neon-lit Tokyo street at night, rain reflections, anamorphic lens."
      />
      <div className="mt-3 space-y-2">
        <div className="space-y-1.5">
          <Label className="text-xs">{t("studio.luma.aspectRatio")}</Label>
          <Select value={aspect} onValueChange={setAspect}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {["16:9", "9:16", "1:1", "4:3", "3:4", "21:9"].map((a) => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <VideoLengthSelector value={videoLength} onChange={setVideoLength} disabled={loading} />
        {/* Starting image from brand library */}
        {startingImageUrl ? (
          <div className="relative rounded-xl overflow-hidden border border-border">
            <img src={startingImageUrl} alt="Starting frame" className="w-full max-h-24 object-cover" />
            <button
              type="button"
              onClick={() => setStartingImageUrl(null)}
              className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-background/80 border border-border flex items-center justify-center hover:bg-destructive/10"
            >
              <X className="w-3 h-3" />
            </button>
            <p className="absolute bottom-1.5 left-2 text-[10px] text-white/80">{t("studio.luma.startingFrame")}</p>
          </div>
        ) : (
          <BrandLibraryButton onSelect={setStartingImageUrl} disabled={loading} label={t("studio.luma.addStartingImage")} />
        )}
      </div>
      <Button
        onClick={() => startGeneration(false)}
        disabled={loading}
        className="w-full mt-3 rounded-full bg-orange-500 hover:bg-orange-600 text-white"
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("studio.luma.renderingBtn")}</>
        ) : (
          <><Wand2 className="w-4 h-4 mr-2" /> {t("studio.luma.generateBtn")}</>
        )}
      </Button>
      {videoUrl && (
        <div className="mt-4">
          <video src={videoUrl} controls className="w-full rounded-xl border border-border" />
          <div className="mt-2 flex items-center justify-between gap-2">
            <a
              href={videoUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center text-xs text-primary hover:underline"
            >
              <Download className="w-3.5 h-3.5 mr-1" /> {t("studio.common.download")}
            </a>
            <div className="flex items-center gap-2">
              {mocked && (
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {t("studio.luma.samplePreview")}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => startGeneration(true)}
                disabled={loading}
                className="text-xs"
              >
                <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                {regenUsed ? t("studio.regen.credit") : t("studio.regen.free")}
              </Button>
            </div>
          </div>
        </div>
      )}
      <p className="mt-3 text-[10px] text-muted-foreground/60 uppercase tracking-wider">{t("studio.luma.poweredBy")}</p>
    </div>
  );
}

type VideoScript = {
  title: string;
  hook: string;
  body: string;
  cta: string;
  on_screen_text?: string[];
  b_roll?: string[];
  voiceover_full: string;
  estimated_duration_seconds: number;
};

function ScriptWriterCard() {
  const { t } = useI18n();
  const [product, setProduct] = useState("");
  const [audience, setAudience] = useState("");
  const [goal, setGoal] = useState("sales");
  const [length, setLength] = useState("medium");
  const [loading, setLoading] = useState(false);
  const [script, setScript] = useState<VideoScript | null>(null);

  const handleGenerate = async () => {
    if (!product.trim()) {
      toast.error(t("ai.toast.tellVideoAbout"));
      return;
    }
    setLoading(true);
    setScript(null);
    const { data, error } = await supabase.functions.invoke("generate-video-script", {
      body: { product: product.trim(), audience: audience.trim(), goal, length },
    });
    setLoading(false);
    if (error || data?.error) {
      toast.error(t("ai.toast.scriptFailed"), { description: data?.error ?? error?.message });
      return;
    }
    setScript(data?.script ?? null);
    toast.success(t("ai.toast.scriptReady"));
  };

  const handleCopy = async () => {
    if (!script) return;
    await navigator.clipboard.writeText(script.voiceover_full);
    toast.success(t("ai.toast.copiedScript"));
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="mb-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <ScrollText className="w-5 h-5 text-primary" />
        </div>
      </div>
      <h3 className="font-semibold">{t("studio.script.title")}</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {t("studio.script.desc")}
      </p>
      <Textarea
        className="mt-4"
        rows={2}
        value={product}
        onChange={(e) => setProduct(e.target.value)}
        placeholder="What's the video about? e.g. A skincare line for sensitive skin."
      />
      <Input
        className="mt-2"
        value={audience}
        onChange={(e) => setAudience(e.target.value)}
        placeholder="Audience (optional) — e.g. Women 25-45"
      />
      <div className="grid grid-cols-2 gap-2 mt-2">
        <Select value={goal} onValueChange={setGoal}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="awareness">{t("studio.goal.awareness")}</SelectItem>
            <SelectItem value="traffic">{t("studio.goal.traffic")}</SelectItem>
            <SelectItem value="leads">{t("studio.goal.leads")}</SelectItem>
            <SelectItem value="sales">{t("studio.goal.sales")}</SelectItem>
            <SelectItem value="app_installs">{t("studio.goal.appInstalls")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={length} onValueChange={setLength}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="short">{t("studio.script.sec15")}</SelectItem>
            <SelectItem value="medium">{t("studio.script.sec30")}</SelectItem>
            <SelectItem value="long">{t("studio.script.sec60")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button
        onClick={handleGenerate}
        disabled={loading}
        className="w-full mt-3 rounded-full bg-orange-500 hover:bg-orange-600 text-white"
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("studio.script.writing")}</>
        ) : (
          <><Wand2 className="w-4 h-4 mr-2" /> {t("studio.script.generateBtn")}</>
        )}
      </Button>
      {script && (
        <div className="mt-4 space-y-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("studio.script.hook")}</div>
            <p className="mt-1">{script.hook}</p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("studio.script.body")}</div>
            <p className="mt-1 whitespace-pre-line">{script.body}</p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("studio.script.cta")}</div>
            <p className="mt-1 font-medium">{script.cta}</p>
          </div>
          {script.on_screen_text?.length ? (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("studio.script.onScreenText")}</div>
              <ul className="mt-1 list-disc list-inside text-muted-foreground">
                {script.on_screen_text.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>
          ) : null}
          {script.b_roll?.length ? (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("studio.script.broll")}</div>
              <ul className="mt-1 list-disc list-inside text-muted-foreground">
                {script.b_roll.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>
          ) : null}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <span className="text-xs text-muted-foreground">~{script.estimated_duration_seconds}s</span>
            <Button variant="outline" size="sm" onClick={handleCopy}>
              <Copy className="w-3.5 h-3.5 mr-1.5" /> {t("studio.script.copyVO")}
            </Button>
          </div>
        </div>
      )}
      <p className="mt-3 text-[10px] text-muted-foreground/60 uppercase tracking-wider">{t("studio.script.poweredBy")}</p>
    </div>
  );
}

function SpanishDubCard() {
  const { t } = useI18n();
  const [sourceUrl, setSourceUrl] = useState("");
  const [dubbingId, setDubbingId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const startDub = async () => {
    if (!sourceUrl.trim()) {
      toast.error(t("ai.toast.pasteEnglishUrl"));
      return;
    }
    setLoading(true);
    setStatus(t("studio.dub.submitting"));
    setAudioUrl(null);
    const { data, error } = await supabase.functions.invoke("dub-spanish", {
      body: { action: "create", sourceUrl: sourceUrl.trim(), targetLanguage: "es", sourceLanguage: "en" },
    });
    if (error || data?.error) {
      setLoading(false);
      setStatus(null);
      toast.error(t("ai.toast.dubFailed"), { description: data?.error ?? error?.message });
      return;
    }
    setDubbingId(data.dubbing_id);
    setStatus("dubbing");
    pollStatus(data.dubbing_id);
  };

  const pollStatus = async (id: string) => {
    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      const { data, error } = await supabase.functions.invoke("dub-spanish", {
        body: { action: "status", dubbingId: id },
      });
      if (error) continue;
      setStatus(data?.status ?? "dubbing");
      if (data?.status === "dubbed") {
        const { data: a, error: aErr } = await supabase.functions.invoke("dub-spanish", {
          body: { action: "audio", dubbingId: id, targetLanguage: "es" },
        });
        if (!aErr && a?.audio) {
          setAudioUrl(`data:${a.mime ?? "audio/mpeg"};base64,${a.audio}`);
          toast.success(t("ai.toast.dubReady"));
        } else {
          toast.error(t("ai.toast.dubAudioFailed"));
        }
        setLoading(false);
        return;
      }
      if (data?.status === "failed") {
        setLoading(false);
        toast.error(t("ai.toast.dubError"), { description: data?.error });
        return;
      }
    }
    setLoading(false);
    toast.error(t("ai.toast.dubTimeout"));
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="mb-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Languages className="w-5 h-5 text-primary" />
        </div>
      </div>
      <h3 className="font-semibold">{t("studio.dub.title")}</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {t("studio.dub.desc")}
      </p>
      <Input
        className="mt-4"
        value={sourceUrl}
        onChange={(e) => setSourceUrl(e.target.value)}
        placeholder="https://… (publicly accessible MP3 or MP4)"
        disabled={loading}
      />
      <Button
        onClick={startDub}
        disabled={loading}
        className="w-full mt-3 rounded-full bg-orange-500 hover:bg-orange-600 text-white"
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {status ?? t("studio.dub.dubbing")}</>
        ) : (
          <><Languages className="w-4 h-4 mr-2" /> {t("studio.dub.generateBtn")}</>
        )}
      </Button>
      {audioUrl && (
        <div className="mt-4">
          <audio src={audioUrl} controls className="w-full" />
          <a
            href={audioUrl}
            download="adkavi-dub-es.mp3"
            className="mt-2 inline-flex items-center text-xs text-primary hover:underline"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> {t("studio.dub.downloadMp3")}
          </a>
        </div>
      )}
      <p className="mt-3 text-[11px] text-muted-foreground">
        {t("studio.dub.timingNote")}
      </p>
      <p className="mt-3 text-[10px] text-muted-foreground/60 uppercase tracking-wider">{t("studio.dub.poweredBy")}</p>
    </div>
  );
}

function BrandLibraryManager() {
  const { t } = useI18n();
  const { images, loading, upload, remove, usedBytes } = useBrandLibrary();
  const storageFull = usedBytes >= STORAGE_LIMIT_BYTES;
  const usagePct = Math.min((usedBytes / STORAGE_LIMIT_BYTES) * 100, 100);
  const [activeCategory, setActiveCategory] = useState<BrandCategory | "all">("all");
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState<BrandCategory>("photo");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const CAT_LABELS: Record<string, string> = {
    photo: t("brand.lib.cat.photo"),
    logo: t("brand.lib.cat.logo"),
    product: t("brand.lib.cat.product"),
    background: t("brand.lib.cat.background"),
    graphic: t("brand.lib.cat.graphic"),
  };

  const visible = activeCategory === "all" ? images : images.filter((img) => img.category === activeCategory);

  async function handleUpload(file: File) {
    if (!file.type.startsWith("image/")) { toast.error(t("brand.lib.err.imagesOnly")); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error(t("brand.lib.err.maxSize")); return; }
    setUploading(true);
    const url = await upload(file, uploadCategory);
    setUploading(false);
    if (url) toast.success(t("brand.lib.success.added"));
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center inline-flex">
              <Images className="w-4 h-4 text-primary" />
            </span>
            {t("brand.lib.title")}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">{t("brand.lib.desc")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={uploadCategory} onValueChange={(v) => setUploadCategory(v as BrandCategory)}>
            <SelectTrigger className="h-8 text-xs w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BRAND_CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value} className="text-xs">{CAT_LABELS[c.value]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            className="h-8 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-xs"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || storageFull}
          >
            {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
            {t("brand.lib.upload")}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); if (fileInputRef.current) fileInputRef.current.value = ""; }}
          />
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {(["all", ...BRAND_CATEGORIES.map((c) => c.value)] as const).map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition ${
              activeCategory === cat
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {cat === "all"
              ? t("brand.lib.catAll", { n: String(images.length) })
              : `${CAT_LABELS[cat] ?? cat} (${images.filter((i) => i.category === cat).length})`}
          </button>
        ))}
      </div>

      {/* Storage usage */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-[11px]">
          <span className={storageFull ? "text-destructive font-medium" : "text-muted-foreground"}>
            {storageFull
              ? t("brand.lib.storageFull")
              : t("brand.lib.storageUsed", { used: formatBytes(usedBytes), total: formatBytes(STORAGE_LIMIT_BYTES) })}
          </span>
          <span className="text-muted-foreground">{usagePct.toFixed(0)}%</span>
        </div>
        <Progress
          value={usagePct}
          className={`h-1.5 ${storageFull ? "[&>div]:bg-destructive" : usagePct > 80 ? "[&>div]:bg-orange-500" : ""}`}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : visible.length === 0 ? (
        <div
          className="border-2 border-dashed border-border rounded-xl p-8 flex flex-col items-center gap-2 cursor-pointer hover:border-primary/40 hover:bg-muted/10 transition"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-6 h-6 text-muted-foreground/40" />
          <p className="text-xs text-muted-foreground text-center">
            {activeCategory === "all"
              ? t("brand.lib.emptyAll")
              : t("brand.lib.emptyCat", { cat: CAT_LABELS[activeCategory]?.toLowerCase() ?? activeCategory })}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {visible.map((img) => (
            <div key={img.id} className="group relative aspect-square rounded-xl overflow-hidden border border-border">
              <img src={img.url} alt={img.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition" />
              <button
                type="button"
                onClick={() => remove(img.id, img.path)}
                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-background/80 border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition hover:bg-destructive/20"
              >
                <Trash2 className="w-3 h-3 text-destructive" />
              </button>
              <div className="absolute bottom-0 inset-x-0 px-1.5 py-1 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition">
                <p className="text-[9px] text-white truncate">{img.name}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
